(function () {
  function isDesktop() {
    var breakpoint = (window.theme && window.theme.sizes && window.theme.sizes.small) || 768;
    return window.innerWidth >= breakpoint;
  }

  function getFlickity(track) {
    return window.theme && window.theme.Flickity ? window.theme.Flickity.data(track) : null;
  }

  // The popped/is-selected tile is taller than the others, so the Flickity
  // viewport needs to resize to it - otherwise it stays locked at the
  // shorter baseline height and the pop overflows into (and gets clipped
  // out of) the section's bottom padding. Flickity's own `adaptiveHeight`
  // option turned out unreliable here (it can end up measuring the cell
  // before the .is-selected height transition finishes), so the height is
  // set directly instead, timed to land after that transition completes.
  function syncViewportHeight(flkty, viewport) {
    var selected = flkty.selectedElement;
    if (!selected) return;
    viewport.style.height = selected.getBoundingClientRect().height + 'px';
  }

  function enableDesktop(track) {
    if (!window.theme || !window.theme.Flickity) return;
    if (getFlickity(track)) return;

    var flkty = new window.theme.Flickity(track, {
      cellSelector: '[data-slide]',
      cellAlign: 'center',
      wrapAround: true,
      pageDots: false,
      adaptiveHeight: false,
    });

    var viewport = track.querySelector('.flickity-viewport');
    if (viewport) viewport.style.transition = 'height 0.4s ease';

    flkty.on('select', function () {
      setTimeout(function () {
        syncViewportHeight(flkty, viewport);
      }, 450);
    });
    syncViewportHeight(flkty, viewport);
    setTimeout(function () {
      syncViewportHeight(flkty, viewport);
    }, 450);

    // Flickity resolves the clicked cell (including any cells it clones
    // internally for wrapAround) itself, which is far more reliable than us
    // trying to compute an index from the DOM after clones are inserted.
    flkty.on('staticClick', function (event, pointer, cellElement, cellIndex) {
      if (cellIndex === null || cellIndex === undefined) return;
      if (!event.target.closest('[data-spotlight-trigger]')) return;
      flkty.select(cellIndex);
    });
  }

  function disableDesktop(track) {
    var flkty = getFlickity(track);
    if (flkty) flkty.destroy();
    track.querySelectorAll('[data-slide]').forEach(function (tile) {
      tile.classList.remove('is-selected');
    });
  }

  // Native scroll carousels can't wrap natively, so we clone the set once on
  // each side and silently jump back into the real content when the user
  // scrolls all the way into a cloned side, keeping the illusion seamless.
  function buildMobileClones(track) {
    if (track.dataset.spotlightCloned) return;
    track.dataset.spotlightCloned = 'true';

    var originals = Array.prototype.slice.call(track.children).filter(function (el) {
      return el.matches('[data-slide]');
    });
    if (originals.length < 2) return;

    function cloneSet() {
      return originals.map(function (el) {
        var clone = el.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach(function (link) {
          link.setAttribute('tabindex', '-1');
        });
        return clone;
      });
    }

    var startClones = cloneSet();
    var endClones = cloneSet();

    var startFragment = document.createDocumentFragment();
    startClones.forEach(function (clone) {
      startFragment.appendChild(clone);
    });
    track.insertBefore(startFragment, track.firstElementChild);

    var endFragment = document.createDocumentFragment();
    endClones.forEach(function (clone) {
      endFragment.appendChild(clone);
    });
    track.appendChild(endFragment);

    bindTriggers(track);
  }

  // The theme applies scroll-behavior: smooth globally, which would animate
  // a plain `track.scrollLeft = x` assignment instead of jumping instantly -
  // exactly the visible "jump back" the wrap illusion needs to avoid.
  function setScrollLeftInstant(track, value) {
    var previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = value;
    requestAnimationFrame(function () {
      track.style.scrollBehavior = previous;
    });
  }

  function setMobileInitialScroll(track) {
    if (track.dataset.spotlightPositioned) return;

    var originals = track.querySelectorAll('[data-slide]:not([aria-hidden="true"])');
    var allClones = track.querySelectorAll('[data-slide][aria-hidden="true"]');
    if (!originals.length || allClones.length < originals.length * 2) return;

    // allClones is in DOM order: [...startClones, ...endClones] - the end
    // clones begin right after the start-clone block, one set length in.
    var firstReal = originals[0];
    var firstEndClone = allClones[originals.length];

    var jump = firstEndClone.getBoundingClientRect().left - firstReal.getBoundingClientRect().left;
    if (!jump) return;

    track.dataset.spotlightPositioned = 'true';
    track._spotlightJump = jump;
    setScrollLeftInstant(track, jump);
  }

  function getCenteredTile(track) {
    var trackRect = track.getBoundingClientRect();
    var centerX = trackRect.left + trackRect.width / 2;
    var tiles = track.querySelectorAll('[data-slide]');
    var closest = null;
    var closestDistance = Infinity;

    tiles.forEach(function (tile) {
      var rect = tile.getBoundingClientRect();
      var distance = Math.abs(rect.left + rect.width / 2 - centerX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = tile;
      }
    });

    return closest;
  }

  // Only jump once scrolling has actually settled on a cloned tile - checking
  // raw scrollLeft against the min/max bounds fires too early, since a
  // snapped position on the last *real* tile can already sit within a
  // pixel or two of that boundary.
  function handleMobileWrap(track) {
    var jump = track._spotlightJump;
    if (!jump) return;

    var centered = getCenteredTile(track);
    if (!centered || centered.getAttribute('aria-hidden') !== 'true') return;

    var originals = track.querySelectorAll('[data-slide]:not([aria-hidden="true"])');
    var allClones = track.querySelectorAll('[data-slide][aria-hidden="true"]');
    var cloneIndex = Array.prototype.indexOf.call(allClones, centered);

    if (cloneIndex < originals.length) {
      setScrollLeftInstant(track, track.scrollLeft + jump); // sitting on a start-clone, jump forward into the real set
    } else {
      setScrollLeftInstant(track, track.scrollLeft - jump); // sitting on an end-clone, jump backward into the real set
    }
  }

  function enableMobile(track) {
    buildMobileClones(track);
    setMobileInitialScroll(track);

    if (track.dataset.spotlightScrollBound) return;
    track.dataset.spotlightScrollBound = 'true';

    track.addEventListener('scroll', function () {
      if (track._spotlightSettleTimer) clearTimeout(track._spotlightSettleTimer);
      track._spotlightSettleTimer = setTimeout(function () {
        handleMobileWrap(track);
      }, 120);
    });
  }

  function setup(track) {
    if (isDesktop()) {
      enableDesktop(track);
    } else {
      disableDesktop(track);
      enableMobile(track);
    }
  }

  function bindTriggers(track) {
    track.querySelectorAll('[data-spotlight-trigger]').forEach(function (trigger) {
      if (trigger.dataset.spotlightBound) return;
      trigger.dataset.spotlightBound = 'true';

      trigger.addEventListener('click', function () {
        if (isDesktop()) return; // handled by Flickity's staticClick

        var cell = trigger.closest('[data-slide]');
        if (cell) cell.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
      });
    });
  }

  function init() {
    var tracks = document.querySelectorAll('.spotlight-carousel__track[data-slider]');
    if (!tracks.length) return;

    function handleResize() {
      tracks.forEach(setup);
    }

    var onResize = window.theme && window.theme.debounce ? window.theme.debounce(handleResize, 200) : handleResize;

    tracks.forEach(function (track) {
      bindTriggers(track);
      setup(track);
    });

    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
